/* eslint-disable react/no-danger */
/* eslint-disable jam3/no-sanitizer-with-danger */
import { FrameDataType } from '@/modules/types/frame';
import React, { ReactElement, useEffect } from 'react';
import { insertUserScript, processFailBehaviourMessage } from './util';
import {
  ElearningPlayerStateType,
  actions as ElearningPlayerActions,
} from '@/components/Player/modules/reducers/elearningPlayer';
import { useSelector, useDispatch } from 'react-redux';
import { FailBehaviour } from '@/modules/types/choice';
import { useFlags } from 'launchdarkly-react-client-sdk';

export interface FrameContentViewPropsType {
  content: string;
  onFrameContentReady: () => void;
  currentFrame: FrameDataType;
}

const FrameContentView = ({
  content,
  onFrameContentReady,
  currentFrame,
}: FrameContentViewPropsType): ReactElement => {
  const dispatch = useDispatch();
  const { learningElaQuizRetryLimit20250128: isQuizAttemptEnabled } =
    useFlags();
  const {
    content: stateContent,
    isLive,
    isPreview,
    handlers,
  }: ElearningPlayerStateType = useSelector(
    (state: { elearningPlayer: ElearningPlayerStateType }) =>
      state.elearningPlayer,
  );
  const { quiz, frames, quizGetAttemptData, currentFrameId } = stateContent;

  useEffect(() => {
    onFrameContentReady();
    if (isLive && quiz && frames && quizGetAttemptData) {
      processFailBehaviourMessage(quiz, quizGetAttemptData, () => {
        handlers.startAssessment(quizGetAttemptData);
        dispatch({
          type: ElearningPlayerActions.enableQuiz.type,
          payload: false,
        });
        handlers.moveToFrame(frames[0].id);
      });
    }
  }, [currentFrame.frameScript, content, quizGetAttemptData]); // run when content is changed only

  useEffect(() => {
    const isTrackingEnabled =
      quiz?.quizMetadata.lmsTrackingEnable && isQuizAttemptEnabled;
    const isFirstFrame = frames && frames[0].id === currentFrameId;
    // Anonymous players (SCORM export, Breathe) never have attempt data
    const hasAttemptData = !!quizGetAttemptData;
    const hasAttemptsLeft =
      quizGetAttemptData?.remainingQuizAttempt &&
      quizGetAttemptData.remainingQuizAttempt > 0;
    const isRestartFailBehaviour =
      quiz?.quizMetadata.failBehaviour === FailBehaviour.restart;

    const shouldEvaluateRestart =
      isFirstFrame &&
      isTrackingEnabled &&
      hasAttemptData &&
      !hasAttemptsLeft &&
      isRestartFailBehaviour &&
      !isPreview &&
      isLive;

    if (shouldEvaluateRestart) {
      handlers.startAssessment(quizGetAttemptData);
    }
  }, [
    frames,
    currentFrameId,
    isLive,
    isPreview,
    quiz,
    quizGetAttemptData,
    isQuizAttemptEnabled,
    handlers,
  ]);

  // Insert script to accommodate event handlers like button clicks
  useEffect(() => {
    insertUserScript(currentFrame.frameScript);

    // Cleanup script elements on component unmount
    return () => {
      if (currentFrame.frameScript) {
        currentFrame.frameScript.forEach((_, index) => {
          const scriptElement = document.getElementById(
            `ela-user-module-script-${index}`,
          );
          if (scriptElement) {
            document.body.removeChild(scriptElement);
          }
        });
      }
    };
  }, [currentFrame.frameScript, content]);

  return (
    <div
      className="canvas-content__inner"
      dangerouslySetInnerHTML={{
        __html: content,
      }}
    />
  );
};

export default FrameContentView;
